<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" dark>
    <v-card style="background-color: #121212">
      <v-toolbar dark color="rgba(30,30,30,0.9)" flat>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="gold-text text-truncate">
          Lektionen · {{ module ? module.title : "" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="#D4AF37" class="black--text" rounded @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon> Lektion hinzufügen
        </v-btn>
      </v-toolbar>

      <div class="pa-4 pa-md-6">
        <div class="d-flex align-center grey--text text-caption mb-3">
          <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
          Reihenfolge per Drag &amp; Drop ändern
        </div>

        <div v-if="lessons.length === 0 && !loading" class="empty-box rounded-lg pa-8 text-center grey--text">
          Noch keine Lektionen. Lege die erste an.
        </div>

        <draggable
          v-else
          :list="lessons"
          handle=".drag-handle"
          :animation="200"
          ghost-class="ghost-row"
          @end="saveOrder"
          item-key="_id">
          <div v-for="(lesson, index) in lessons" :key="lesson._id" class="lesson-row rounded-lg pa-3 mb-2 d-flex align-center">
            <v-icon class="drag-handle mr-2" color="grey">mdi-drag-vertical</v-icon>
            <div class="lesson-index mr-3">{{ index + 1 }}</div>
            <v-icon :color="typeColor(lesson.contentType)" class="mr-3">{{ typeIcon(lesson.contentType) }}</v-icon>
            <div class="flex-grow-1" style="min-width: 0">
              <div class="font-weight-bold text-truncate">{{ lesson.title }}</div>
              <div class="text-caption grey--text">
                <span class="text-capitalize">{{ lesson.contentType }}</span>
                · {{ lesson.estimatedMinutes || 0 }} Min
              </div>
            </div>
            <v-btn icon small color="blue" @click="editLesson(lesson)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small color="red" @click="deleteLesson(lesson)"><v-icon small>mdi-delete</v-icon></v-btn>
          </div>
        </draggable>

        <v-progress-linear v-if="loading" indeterminate color="#D4AF37"></v-progress-linear>
      </div>
    </v-card>

    <!-- Create/Edit Lesson Dialog -->
    <v-dialog v-model="editDialog" max-width="800px" persistent dark scrollable>
      <v-card style="background-color: #1e1e1e">
        <v-card-title>
          <span class="text-h5 gold-text">{{ editingLesson ? "Lektion bearbeiten" : "Neue Lektion" }}</span>
          <v-spacer />
          <v-btn icon @click="editDialog = false" :disabled="uploading"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text style="max-height: 70vh">
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.title" label="Titel" :rules="[(v) => !!v || 'Erforderlich']" required outlined dense></v-text-field>

            <!-- Modus: Datei vs. Text. Bei Datei wird der Typ automatisch erkannt. -->
            <v-btn-toggle v-model="lessonMode" mandatory rounded dense class="mb-3 d-flex" background-color="transparent" dark>
              <v-btn small value="media" class="flex-grow-1"><v-icon left small>mdi-upload</v-icon> Datei</v-btn>
              <v-btn small value="text" class="flex-grow-1"><v-icon left small>mdi-text-box-outline</v-icon> Text</v-btn>
            </v-btn-toggle>

            <v-text-field v-model.number="form.estimatedMinutes" label="Geschätzte Dauer (Min)" type="number" outlined dense></v-text-field>

            <!-- Erkannten Typ als kleinen Indikator anzeigen -->
            <div v-if="lessonMode === 'media' && form.contentType && form.contentType !== 'text'" class="detected-type d-flex align-center mb-3">
              <v-icon small :color="typeColor(form.contentType)" class="mr-1">{{ typeIcon(form.contentType) }}</v-icon>
              <span class="text-caption grey--text">Erkannter Typ:</span>
              <span class="ml-1 text-caption font-weight-bold white--text text-capitalize">{{ form.contentType }}</span>
              <v-spacer />
              <v-btn x-small text color="grey" @click="manualTypeOverride = !manualTypeOverride">
                <v-icon x-small left>mdi-pencil</v-icon>
                {{ manualTypeOverride ? "Auto" : "ändern" }}
              </v-btn>
            </div>
            <v-select
              v-if="lessonMode === 'media' && manualTypeOverride"
              v-model="form.contentType"
              :items="contentTypeOptions.filter((o) => o.value !== 'text')"
              item-text="label"
              item-value="value"
              label="Typ überschreiben"
              outlined
              dense
              class="mb-3">
              <template v-slot:selection="{ item }">
                <v-icon left small :color="typeColor(item.value)">{{ typeIcon(item.value) }}</v-icon>
                {{ item.label }}
              </template>
              <template v-slot:item="{ item }">
                <v-icon left small :color="typeColor(item.value)">{{ typeIcon(item.value) }}</v-icon>
                {{ item.label }}
              </template>
            </v-select>

            <v-textarea v-model="form.introduction" label="Einleitungstext" outlined dense rows="3"></v-textarea>

            <!-- MEDIA-SEKTION (analog Toolbox) -->
            <template v-if="lessonMode === 'media'">
              <div class="d-flex align-center mb-3 mt-2">
                <v-icon color="#D4AF37" class="mr-2">mdi-paperclip</v-icon>
                <h4 class="text-subtitle-1">Medien-Datei</h4>
                <span class="text-caption grey--text ml-2">(eine Datei pro Lektion)</span>
              </div>

              <!-- Bestehendes Media als Karte -->
              <v-card v-if="editingLesson && editingLesson.mediaFile && editingLesson.mediaFile.fileId && !replacingMedia" class="media-item rounded-lg pa-3 mb-3" dark elevation="0">
                <div class="d-flex align-start mb-2">
                  <div class="media-icon mr-3">
                    <v-icon size="26" :color="typeColor(form.contentType)">{{ typeIcon(form.contentType) }}</v-icon>
                  </div>
                  <div class="flex-grow-1" style="min-width: 0">
                    <div class="font-weight-bold text-truncate">{{ editingLesson.mediaFile.originalName }}</div>
                    <div class="text-caption grey--text text-uppercase">
                      {{ form.contentType }}
                      <template v-if="editingLesson.mediaFile.size"> · {{ formatSize(editingLesson.mediaFile.size) }}</template>
                    </div>
                  </div>
                  <v-btn icon x-small color="grey" @click="replacingMedia = true" title="Ersetzen">
                    <v-icon small>mdi-swap-horizontal</v-icon>
                  </v-btn>
                </div>
                <div class="media-preview">
                  <SecuredMedia :src="lessonStreamPath(editingLesson._id)" :media-type="form.contentType" eager />
                </div>
              </v-card>

              <!-- Upload-Zone (neu oder beim Ersetzen) -->
              <div v-if="!editingLesson || !editingLesson.mediaFile || !editingLesson.mediaFile.fileId || replacingMedia" class="upload-zone rounded-lg pa-4 mb-3">
                <v-file-input
                  v-model="mediaFile"
                  :label="replacingMedia ? 'Neue Datei wählen (ersetzt die alte beim Speichern)' : 'Datei wählen'"
                  outlined
                  dense
                  hide-details
                  prepend-icon="mdi-upload"
                  :rules="editingLesson || replacingMedia ? [fileSizeRule] : [(v) => !!v || 'Datei erforderlich', fileSizeRule]"></v-file-input>
                <div v-if="replacingMedia" class="d-flex justify-end mt-2">
                  <v-btn text small color="grey" @click="cancelReplace">Abbrechen</v-btn>
                </div>
              </div>
            </template>

            <v-textarea v-if="lessonMode === 'text'" v-model="form.textContent" label="Inhalt (HTML / Text)" outlined rows="6"></v-textarea>
          </v-form>

          <v-progress-linear v-if="uploading" :value="uploadProgress" color="#D4AF37" height="10" striped class="mt-3"></v-progress-linear>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="editDialog = false" :disabled="uploading">Abbrechen</v-btn>
          <v-btn color="#D4AF37" class="black--text" @click="saveLesson" :disabled="!valid || uploading" :loading="uploading">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import api from "@/api"
import draggable from "vuedraggable"
import SecuredMedia from "../SecuredMedia.vue"

export default {
  name: "LessonEditor",
  components: { draggable, SecuredMedia },
  props: {
    value: Boolean,
    module: Object,
  },
  data() {
    return {
      loading: false,
      lessons: [],
      editDialog: false,
      valid: false,
      editingLesson: null,
      mediaFile: null,
      replacingMedia: false,
      uploading: false,
      uploadProgress: 0,
      lessonMode: "media",
      manualTypeOverride: false,
      contentTypeOptions: [
        { value: "video", label: "Video" },
        { value: "audio", label: "Audio" },
        { value: "image", label: "Bild" },
        { value: "pdf", label: "PDF / Dokument" },
        { value: "text", label: "Text / HTML" },
      ],
      form: {
        title: "",
        contentType: "video",
        estimatedMinutes: 5,
        introduction: "",
        textContent: "",
      },
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
    fileSizeRule() {
      return (v) => {
        if (!v) return true
        if (v.type && v.type.startsWith("video/") && v.size > 500 * 1024 * 1024) return "Video max. 500MB"
        if (v.type && v.type.startsWith("audio/") && v.size > 50 * 1024 * 1024) return "Audio max. 50MB"
        if (v.type && v.type.startsWith("image/") && v.size > 10 * 1024 * 1024) return "Bild max. 10MB"
        return true
      }
    },
  },
  watch: {
    dialog(val) {
      if (val && this.module) this.loadLessons()
    },
    mediaFile(file) {
      // Auto-Detect des Typs aus der MIME – User kann danach trotzdem manuell überschreiben
      if (file && file.type) {
        if (file.type.startsWith("video/")) this.form.contentType = "video"
        else if (file.type.startsWith("audio/")) this.form.contentType = "audio"
        else if (file.type.startsWith("image/")) this.form.contentType = "image"
        else if (file.type === "application/pdf") this.form.contentType = "pdf"
        else this.form.contentType = "file"
      }
    },
    lessonMode(mode) {
      if (mode === "text") this.form.contentType = "text"
      else if (this.form.contentType === "text") this.form.contentType = "video"
    },
  },
  methods: {
    async loadLessons() {
      this.loading = true
      try {
        const mod = await api.akademie.fetchModule(this.module._id || this.module.id)
        this.lessons = (mod && mod.lessons) || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    close() {
      this.dialog = false
    },
    openCreateDialog() {
      this.editingLesson = null
      this.mediaFile = null
      this.replacingMedia = false
      this.lessonMode = "media"
      this.manualTypeOverride = false
      this.form = { title: "", contentType: "video", estimatedMinutes: 5, introduction: "", textContent: "" }
      this.editDialog = true
    },
    cancelReplace() {
      this.replacingMedia = false
      this.mediaFile = null
    },
    formatSize(b) {
      if (!b) return ""
      if (b < 1024) return b + " B"
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB"
      return (b / 1024 / 1024).toFixed(1) + " MB"
    },
    editLesson(item) {
      this.editingLesson = item
      this.mediaFile = null
      this.replacingMedia = false
      this.lessonMode = item.contentType === "text" ? "text" : "media"
      this.manualTypeOverride = false
      this.form = {
        title: item.title || "",
        contentType: item.contentType || "video",
        estimatedMinutes: item.estimatedMinutes || 0,
        introduction: item.introduction || "",
        textContent: item.textContent || "",
      }
      this.editDialog = true
    },
    async saveLesson() {
      if (!this.$refs.form.validate()) return
      this.uploading = true
      this.uploadProgress = 0
      try {
        if (this.editingLesson) {
          await api.akademie.updateLesson(this.editingLesson._id, this.form, this.replacingMedia ? this.mediaFile : null, (ev) => {
            this.uploadProgress = Math.round((ev.loaded * 100) / Math.max(1, ev.total))
          })
        } else {
          const fd = new FormData()
          fd.append("title", this.form.title)
          fd.append("contentType", this.form.contentType)
          fd.append("estimatedMinutes", this.form.estimatedMinutes)
          fd.append("introduction", this.form.introduction || "")
          fd.append("textContent", this.form.textContent || "")
          if (this.mediaFile) fd.append("media", this.mediaFile)

          await api.akademie.uploadLesson(this.module._id || this.module.id, fd, (ev) => {
            this.uploadProgress = Math.round((ev.loaded * 100) / Math.max(1, ev.total))
          })
        }
        this.editDialog = false
        this.loadLessons()
      } catch (e) {
        console.error("Save error", e)
      } finally {
        this.uploading = false
      }
    },
    async deleteLesson(item) {
      if (!confirm(`Lektion "${item.title}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteLesson(item._id)
        this.loadLessons()
      } catch (e) {
        console.error(e)
      }
    },
    async saveOrder() {
      const ids = this.lessons.map((l) => l._id)
      try {
        await api.akademie.reorderLessons(this.module._id || this.module.id, ids)
      } catch (e) {
        console.error("Reorder error", e)
        this.loadLessons()
      }
    },
    lessonStreamPath(id) {
      return api.akademie.getLessonStreamPath(id)
    },
    typeIcon(t) {
      const map = {
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        image: "mdi-image-outline",
        text: "mdi-text-box-outline",
        pdf: "mdi-file-pdf-box",
        file: "mdi-file-outline",
      }
      return map[t] || "mdi-file-outline"
    },
    typeColor(t) {
      const map = { video: "#ef5350", audio: "#ab47bc", image: "#42a5f5", text: "#66bb6a", pdf: "#e53935", file: "#bdbdbd" }
      return map[t] || "grey"
    },
  },
}
</script>

<style scoped>
.gold-text { color: #d4af37 !important; }

.lesson-row {
  background: rgba(30, 30, 30, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: border-color 0.2s, transform 0.2s;
}
.lesson-row:hover {
  border-color: rgba(212, 175, 55, 0.4);
}
.lesson-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(212, 175, 55, 0.15);
  color: #d4af37;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.drag-handle { cursor: grab; }
.drag-handle:active { cursor: grabbing; }
.ghost-row {
  opacity: 0.4;
  background: rgba(212, 175, 55, 0.1) !important;
}
.empty-box {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.media-item {
  background: rgba(20, 20, 20, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.media-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}
.media-preview :deep(.secured-media-content) {
  max-height: 280px;
  border-radius: 8px;
}
.upload-zone {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(212, 175, 55, 0.25);
}
.detected-type {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 6px 10px;
}
</style>
